// Fase 2 não-DEK passo 3 (ADR-0008) — Aba "Oitivas" do CaseDetailPage.
//
// Modelo γ: cada Oitiva/DispensaOitiva carrega `status` ('anexada' |
// 'pendente_anexo'). O registro nasce 'pendente_anexo'; a state machine bloqueia
// close_oitivas enquanto houver QUALQUER pendência — fechamento exige conteúdo real.
//
// O conteúdo (ata + presentes + justificativa) é anexado via PATCH
// /oitiva/:oitivaId/attach-ata (Fatia 5 — ADR-0008 "resolvido por Fatia 5"),
// cifrado no navegador com a DEK do caso (AttachAtaForm + case-crypto). Inerte
// atrás de COMITE_READ_ENABLED: sem a leitura viva o membro não reabre a DEK →
// o anexo fica indisponível e o registro segue pendente (não fabricamos evidência,
// ADR-0008). Paridade ADR-0003 NÃO se aplica (oitiva é registro processual da
// Lei 14.457; texto pro parecer-ia é apoio à decisão).
//
// B1 (2026-06-23): migrado do tema CLARO (cores hardcoded #faf6ee/white/#fff8e7,
// regressão do redesign DARK de 2026-06-09 — texto quase-branco em card branco)
// para os primitivos theme-aware Card/Badge/Alert — os MESMOS blocos das abas
// Pareceres (Card+Badge) e Descrição (Alert). Só apresentação: zero mudança de
// lógica/estado/handlers.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../contexts/SessionContext';
import { decryptPayload } from '@aions/crypto';
import { Card, Badge, Alert, Button } from '@aions/ui-primitives';
import type { DenunciaState, Oitiva, OitivaOuDispensa, DispensaOitiva } from '@aions/core';
import { apiFetch, logout } from '../lib/auth-client';
// Fatia 5 (Trilha 3) — anexa a ata/justificativa CIFRADA via a fundação de escrita.
import { encryptCaseFields, withCaseDek, type CaseDekAccess } from '../lib/case-crypto';
import { kekMortaNestaSessao } from '../lib/kek-member-session';
import { base64ToBytes } from '../lib/bytes';
// #4 Termo de Declaração (FP-CMP-007) — gera o PDF por oitiva anexada.
import { renderForm } from '../lib/pdf/form-pdf';
import { buildDeclaracao } from '../lib/pdf/form-declaracao';
import type { FormInput, MemberInfo } from '../lib/pdf/forms-common';

interface Props {
  caseId: string;
  /** Categoria do caso — só usada pelo cabeçalho do Termo de Declaração (#4). */
  categoria: string;
  state: DenunciaState;
  /** Callback para refetch do CaseDetail após mutação. */
  onMutate: () => void;
  /** Fatia 5 — acesso à DEK do caso (resolvido pelo CaseDetailPage). status!=='ok'
   *  (flag OFF / sem keypair / sem acesso) → anexo indisponível, registro fica pendente. */
  caseCrypto: CaseDekAccess;
}

function extractOitivas(state: DenunciaState): ReadonlyArray<OitivaOuDispensa> {
  if (
    state.kind === 'Oitivas' ||
    state.kind === 'AvaliacaoEmCurso' ||
    state.kind === 'AguardandoDecisao' ||
    state.kind === 'Decisao'
  ) {
    return state.oitivas;
  }
  return [];
}

function isDispensa(o: OitivaOuDispensa): o is DispensaOitiva {
  return 'justificativaCiphertext' in o;
}

function formatData(epochMs: number): string {
  try {
    return new Date(epochMs).toLocaleString('pt-BR');
  } catch {
    return String(epochMs);
  }
}

// Estilos de texto — tokens do tema (sem hex hardcoded). O "card" vem do
// primitivo Card (.cf-card glass dark), idêntico às abas vizinhas.
const HELP_STYLE: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 12,
  lineHeight: 1.55,
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  marginBottom: 8,
  color: 'var(--color-text-primary)',
};

/** Fatia 5 — formulário de anexo de UMA oitiva/dispensa pendente. Cifra o conteúdo
 *  no navegador (encryptCaseFields, AAD=caseId) e faz PATCH attach-ata. Estado local
 *  por-oitiva (cada card pendente tem o seu). Quando a leitura não está habilitada
 *  (caseCrypto.status !== 'ok'), mostra aviso honesto e NÃO permite anexar (o registro
 *  segue pendente — não fabricamos evidência, ADR-0008). */
function AttachAtaForm({
  oitiva,
  caseId,
  caseCrypto,
  onDone,
}: {
  oitiva: OitivaOuDispensa;
  caseId: string;
  caseCrypto: CaseDekAccess;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const dispensa = isDispensa(oitiva);
  const [ata, setAta] = useState('');
  const [presentes, setPresentes] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Regra 1 — sonda da KEK no MOUNT, antes de o membro digitar a ata. O hook fica
  // ANTES do early-return abaixo (rules-of-hooks: retorno condicional não pode vir
  // antes de um hook). Deps: [temAcesso] — booleano derivado de caseCrypto.status,
  // que é a ÚNICA coisa reativa que o efeito lê; caseCrypto inteiro nas deps faria
  // o efeito re-rodar a cada render do pai (objeto novo), sondando sem motivo.
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  const temAcesso = caseCrypto.status === 'ok';
  useEffect(() => {
    if (!temAcesso) return;
    let cancelled = false;
    void (async () => {
      // Sem catch local: `kekMortaNestaSessao` NUNCA lança e só devolve true com
      // determinação positiva (ver o contrato na lib — aviso antecipado, não gate).
      const morta = await kekMortaNestaSessao();
      if (!cancelled && morta) setSessaoExpirada(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [temAcesso]);

  if (caseCrypto.status !== 'ok') {
    return (
      <Alert variant="info" role="status" style={{ marginTop: 10 }}>
        {t('oitivas.anexar_indisponivel')}
      </Alert>
    );
  }
  const access = caseCrypto; // narrow {status:'ok'} p/ encryptCaseFields

  const filled = dispensa
    ? justificativa.trim() !== ''
    : ata.trim() !== '' && presentes.trim() !== '';

  async function handleAttach(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Cifra o(s) campo(s) com a DEK do caso (AAD=caseId), numa única abertura.
      // O DEK vive só no callback de encryptCaseFields; só ciphertext sai daqui.
      // Ramos separados para o TS estreitar o retorno de encryptCaseFields.
      let body: Record<string, string>;
      if (dispensa) {
        const enc = await encryptCaseFields(access, caseId, { justificativa });
        body = {
          justificativaCiphertext: enc.justificativa.ciphertext,
          nonce: enc.justificativa.nonce,
        };
      } else {
        const enc = await encryptCaseFields(access, caseId, { ata, attendees: presentes });
        body = {
          ataCiphertext: enc.ata.ciphertext,
          ataNonce: enc.ata.nonce,
          attendeesCiphertext: enc.attendees.ciphertext,
          attendeesNonce: enc.attendees.nonce,
        };
      }
      const res = await apiFetch(`/api/comite/cases/${caseId}/oitiva/${oitiva.ulid}/attach-ata`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: string };
        setError(b.error ?? 'attach_failed');
        return;
      }
      onDone();
    } catch {
      // Falha de cripto no browser (KEK ausente pós hard-refresh, etc).
      setError('encrypt_failed');
    } finally {
      setBusy(false);
    }
  }

  const taStyle: React.CSSProperties = {
    width: '100%',
    minHeight: 64,
    padding: 8,
    fontFamily: 'inherit',
    marginBottom: 8,
  };

  return (
    <div
      style={{
        marginTop: 12,
        borderTop: '1px solid var(--color-border, #33405a)',
        paddingTop: 12,
      }}
    >
      <div style={SECTION_HEADER_STYLE}>
        {t(dispensa ? 'oitivas.anexar_dispensa_label' : 'oitivas.anexar_oitiva_label')}
      </div>
      <p style={HELP_STYLE}>{t('oitivas.anexar_help')}</p>
      {dispensa ? (
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {t('oitivas.anexar_justificativa_field')}
          </span>
          <textarea
            style={taStyle}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
          />
        </label>
      ) : (
        <>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {t('oitivas.anexar_ata_field')}
            </span>
            <textarea style={taStyle} value={ata} onChange={(e) => setAta(e.target.value)} />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {t('oitivas.anexar_presentes_field')}
            </span>
            <textarea
              style={taStyle}
              value={presentes}
              onChange={(e) => setPresentes(e.target.value)}
            />
          </label>
        </>
      )}
      {error !== null && (
        <Alert variant="error" style={{ marginBottom: 8 }}>
          {t(`oitivas.errors.${error}`, { defaultValue: t('oitivas.errors.attach_failed') })}
        </Alert>
      )}
      {sessaoExpirada && (
        <Alert
          variant="warning"
          role="status"
          aria-live="polite"
          title={t('case_detail.sessao_expirada_title')}
          style={{ marginBottom: 12 }}
        >
          <div>{t('case_detail.sessao_expirada_body')}</div>
          {/* Incondicional, e é DECLARAÇÃO DE CONSEQUÊNCIA, não instrução: "copie o
              texto" assumiria que existe texto — condição que pode errar (a sonda é
              assíncrona e o campo fica ativo até ela responder). "Descarta qualquer
              texto não enviado" é verdade com ou sem rascunho. */}
          <div style={{ marginTop: 6 }}>{t('case_detail.sessao_expirada_descarte')}</div>
          <div style={{ marginTop: 10 }}>
            <Button
              variant="primary"
              size="sm"
              data-testid="sessao-expirada-entrar"
              onClick={() => {
                void (async () => {
                  await logout();
                  setSession(null);
                })();
              }}
            >
              {t('case_detail.sessao_expirada_acao')}
            </Button>
          </div>
        </Alert>
      )}
      <Button
        loading={busy}
        disabled={!filled || busy || sessaoExpirada}
        onClick={() => void handleAttach()}
      >
        {busy ? t('oitivas.anexando') : t('oitivas.anexar_button')}
      </Button>
    </div>
  );
}

/** #4 — botão "Gerar Termo de Declaração (PDF)" para UMA oitiva ANEXADA. Abre a DEK
 *  (withCaseDek), decifra ata (depoimento) + presentes, monta o PDF client-side e
 *  baixa. Conteúdo cifrado nunca sai daqui — só o PDF gerado no browser. Sem caseCrypto
 *  'ok' (flag OFF / sem acesso) não renderiza (paridade do anexo). */
function DeclaracaoButton({
  oitiva,
  caseId,
  categoria,
  state,
  caseCrypto,
}: {
  oitiva: Oitiva;
  caseId: string;
  categoria: string;
  state: DenunciaState;
  caseCrypto: CaseDekAccess;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (caseCrypto.status !== 'ok') return null;
  const access = caseCrypto;

  async function handleGerar(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Fetch lazy do diretório de membros (rótulo do registrante).
      let members: ReadonlyArray<MemberInfo> = [];
      const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
      if (resp.ok) members = ((await resp.json()) as { members: MemberInfo[] }).members;

      // Uma abertura de DEK decifra ata (depoimento) + presentes (AAD=caseId).
      const { depoimento, presentes } = await withCaseDek(access, async (dek) => {
        const dep = oitiva.ataCiphertext
          ? await decryptPayload(
              base64ToBytes(oitiva.ataCiphertext),
              base64ToBytes(oitiva.nonce),
              dek,
              caseId,
            )
          : '';
        const pres = oitiva.attendeesCiphertext
          ? await decryptPayload(
              base64ToBytes(oitiva.attendeesCiphertext),
              base64ToBytes(oitiva.attendeesNonce),
              dek,
              caseId,
            )
          : '';
        return { depoimento: dep, presentes: pres };
      });

      // createdWeek/attachments não são usados pelo Termo (buildDeclaracao os ignora).
      const input: FormInput = {
        caseId,
        categoria,
        createdWeek: '',
        state,
        attachments: [],
        members,
      };
      const blob = renderForm(
        buildDeclaracao(input, {
          depoimento,
          presentes,
          dataOitiva: oitiva.registeredAt,
          registranteUlid: oitiva.registeredBy,
        }),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `termo-declaracao-${caseId.slice(0, 12)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('declaracao_failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <Button variant="ghost" loading={busy} disabled={busy} onClick={() => void handleGerar()}>
        {busy ? t('oitivas.gerando_declaracao') : t('oitivas.gerar_declaracao')}
      </Button>
      {error !== null && (
        <Alert variant="error" style={{ marginTop: 8 }}>
          {t(`oitivas.errors.${error}`, { defaultValue: t('oitivas.errors.declaracao_failed') })}
        </Alert>
      )}
    </div>
  );
}

export function OitivaPanel({
  caseId,
  categoria,
  state,
  onMutate,
  caseCrypto,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const oitivas = extractOitivas(state);
  const fasePermitida = state.kind === 'Oitivas';
  // C2 (Parede C) — registrar oitiva/dispensa é permitido também em Instrução: o
  // 1º registro dispara open_oitivas (Instrucao→Oitivas) no servidor (addOitiva,
  // paridade InMemory/Supabase). Anexar e encerrar seguem só na fase Oitivas.
  const podeRegistrar = state.kind === 'Instrucao' || state.kind === 'Oitivas';
  const pendentesCount = oitivas.filter((o) => o.status === 'pendente_anexo').length;
  const podeEncerrar = fasePermitida && oitivas.length > 0 && pendentesCount === 0;

  const [submitting, setSubmitting] = useState<'oitiva' | 'dispensa' | 'close' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function postEndpoint(suffix: string, kind: typeof submitting): Promise<void> {
    if (submitting !== null) return;
    setSubmitting(kind);
    setError(null);
    try {
      const res = await apiFetch(`/api/comite/cases/${caseId}${suffix}`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? (kind === 'close' ? 'close_failed' : 'registrar_failed'));
        return;
      }
      onMutate();
    } catch {
      setError('network');
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{t('oitivas.title')}</h3>
      <p style={HELP_STYLE}>{t('oitivas.intro')}</p>

      {/* Aviso opção γ — explica por que o registro nasce pendente. Mesmo
          primitivo Alert que as bordas de leitura da aba Descrição. */}
      <Alert
        variant="warning"
        title={t('oitivas.pendente_anexo_aviso_title')}
        style={{ marginBottom: 16 }}
      >
        {t('oitivas.pendente_anexo_aviso_body')}
      </Alert>

      {/* Lista de oitivas/dispensas */}
      {oitivas.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
          {t('oitivas.lista_vazia')}
        </p>
      ) : (
        <div style={{ margin: '0 0 24px' }}>
          {oitivas.map((o) => {
            const dispensa = isDispensa(o);
            const tipo = dispensa ? t('oitivas.tipo_dispensa') : t('oitivas.tipo_oitiva');
            const por = dispensa
              ? (o as DispensaOitiva).autorizadoPor
              : (o as Exclude<OitivaOuDispensa, DispensaOitiva>).registeredBy;
            const quando = dispensa
              ? (o as DispensaOitiva).autorizadoEm
              : (o as Exclude<OitivaOuDispensa, DispensaOitiva>).registeredAt;
            const anexada = o.status === 'anexada';
            const statusLabel = anexada
              ? dispensa
                ? t('oitivas.status_anexada_dispensa')
                : t('oitivas.status_anexada')
              : t('oitivas.status_pendente');
            return (
              <Card key={o.ulid} padding="sm" style={{ marginBottom: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <strong style={{ fontSize: '0.95rem' }}>{tipo}</strong>
                  <Badge tone={anexada ? 'success' : 'amber'}>{statusLabel}</Badge>
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                  {t('oitivas.lista_item_data', { data: formatData(quando) })}{' '}
                  {t('oitivas.lista_item_por', { membro: por })}
                </div>
                {/* Fatia 5 — anexo do conteúdo cifrado (só para pendentes, na fase Oitivas). */}
                {!anexada && fasePermitida && (
                  <AttachAtaForm
                    oitiva={o}
                    caseId={caseId}
                    caseCrypto={caseCrypto}
                    onDone={onMutate}
                  />
                )}
                {/* #4 — Termo de Declaração: só para Oitiva ANEXADA (dispensa não gera). */}
                {anexada && !dispensa && (
                  <DeclaracaoButton
                    oitiva={o as Oitiva}
                    caseId={caseId}
                    categoria={categoria}
                    state={state}
                    caseCrypto={caseCrypto}
                  />
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Cards de ação. Registrar (oitiva/dispensa) em Instrução+Oitivas (podeRegistrar);
          encerrar só em Oitivas (fasePermitida). Fora dessas fases → aviso. */}
      {!podeRegistrar ? (
        <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
          {t('oitivas.fase_nao_permitida')}
        </p>
      ) : (
        <>
          <Card padding="md" style={{ marginBottom: 16 }}>
            <div style={SECTION_HEADER_STYLE}>{t('oitivas.registrar_oitiva_label')}</div>
            <p style={HELP_STYLE}>{t('oitivas.registrar_oitiva_help')}</p>
            <Button
              loading={submitting === 'oitiva'}
              disabled={submitting !== null}
              onClick={() => void postEndpoint('/oitiva', 'oitiva')}
            >
              {t('oitivas.registrar_oitiva_button')}
            </Button>
          </Card>

          <Card padding="md" style={{ marginBottom: 16 }}>
            <div style={SECTION_HEADER_STYLE}>{t('oitivas.registrar_dispensa_label')}</div>
            <p style={HELP_STYLE}>{t('oitivas.registrar_dispensa_help')}</p>
            <Button
              variant="ghost"
              loading={submitting === 'dispensa'}
              disabled={submitting !== null}
              onClick={() => void postEndpoint('/dispensa-oitiva', 'dispensa')}
            >
              {t('oitivas.registrar_dispensa_button')}
            </Button>
          </Card>

          {/* Encerrar fase — só em Oitivas (em Instrução ainda não há registros). */}
          {fasePermitida && (
            <Card padding="md" style={{ marginBottom: 16 }}>
              <div style={SECTION_HEADER_STYLE}>{t('oitivas.encerrar_fase_label')}</div>
              <p style={HELP_STYLE}>{t('oitivas.encerrar_fase_help')}</p>
              <Button
                loading={submitting === 'close'}
                disabled={!podeEncerrar || submitting !== null}
                onClick={() => void postEndpoint('/close-oitivas', 'close')}
                title={
                  oitivas.length === 0
                    ? t('oitivas.encerrar_fase_disabled_sem_registros')
                    : pendentesCount > 0
                      ? t('oitivas.encerrar_fase_disabled_pendencias', { count: pendentesCount })
                      : undefined
                }
              >
                {t('oitivas.encerrar_fase_button')}
              </Button>
              {!podeEncerrar && oitivas.length === 0 && (
                <p
                  style={{
                    marginTop: 8,
                    fontSize: '0.85rem',
                    color: 'var(--color-text-secondary)',
                    fontStyle: 'italic',
                  }}
                >
                  {t('oitivas.encerrar_fase_disabled_sem_registros')}
                </p>
              )}
              {!podeEncerrar && pendentesCount > 0 && (
                <p
                  style={{
                    marginTop: 8,
                    fontSize: '0.85rem',
                    color: 'var(--color-warning)',
                    fontStyle: 'italic',
                  }}
                >
                  {t('oitivas.encerrar_fase_disabled_pendencias', { count: pendentesCount })}
                </p>
              )}
            </Card>
          )}
        </>
      )}

      {error !== null && (
        <Alert variant="error" style={{ marginTop: 16 }}>
          {t(`oitivas.errors.${error}`, { defaultValue: t('oitivas.errors.registrar_failed') })}
        </Alert>
      )}
    </div>
  );
}
